import { useTranslation } from '../presentation/common/useTranslation.js';
import { Moon, Sun } from 'lucide-react';

import { useTheme } from '../hooks/useTheme';

export default function ThemeSwitcher({ variant = 'compact', className = '' }) {
  const { t } = useTranslation();
  const { theme, setTheme, toggleTheme, isDark, isLight } = useTheme();

  const darkLabel = t('profile_v2.dark', 'Dunkel');
  const lightLabel = t('profile_v2.light', 'Hell');

  if (variant === 'toggle') {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={isDark ? `${lightLabel} aktivieren` : `${darkLabel} aktivieren`}
        title={isDark ? `${lightLabel} (Bright Mode)` : `${darkLabel} (Dark Mode)`}
        className={`world-appearance-toggle inline-flex items-center justify-center p-2 rounded-full border transition-colors ${
          isLight
            ? 'border-[#c6b68e] bg-[#fffdf7] text-[#795819] hover:bg-[#eee9dc]'
            : 'border-[#53606c] bg-[#08141f] text-[#eed8ab] hover:border-[#bda16b]'
        } ${className}`}
      >
        {isLight ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    );
  }

  if (variant === 'full') {
    return (
      <div className={`flex gap-2 ${className}`}>
        <button
          type="button"
          onClick={() => setTheme('dark')}
          aria-pressed={isDark}
          className={`world-button px-3 py-2 border text-sm flex items-center justify-center gap-2 transition-colors ${
            isDark ? 'border-amber-500 text-amber-300' : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
          }`}
        >
          <Moon className="w-4 h-4" />
          <span>{darkLabel}</span>
        </button>
        <button
          type="button"
          onClick={() => setTheme('light')}
          aria-pressed={isLight}
          className={`world-button px-3 py-2 border text-sm flex items-center justify-center gap-2 transition-colors ${
            isLight ? 'border-amber-500 text-amber-300' : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
          }`}
        >
          <Sun className="w-4 h-4" />
          <span>{lightLabel}</span>
        </button>
      </div>
    );
  }

  // Compact segmented toggle for headers / topbars
  return (
    <div
      role="group"
      aria-label={t('profile_v2.theme', 'Design')}
      className={`world-theme-switcher inline-flex items-center p-1 text-xs ${className}`}
    >
      <button
        type="button"
        onClick={() => setTheme('dark')}
        aria-pressed={isDark}
        className={`px-2 py-1 transition-colors flex items-center justify-center ${
          isDark ? 'is-active' : ''
        }`}
        title={`${darkLabel} (Dark Mode)`}
        aria-label={`${darkLabel} (Dark Mode)`}
      >
        <Moon className="w-3.5 h-3.5" />
      </button>
      <div className="h-3 w-px bg-zinc-700/50" />
      <button
        type="button"
        onClick={() => setTheme('light')}
        aria-pressed={isLight}
        className={`px-2 py-1 transition-colors flex items-center justify-center ${
          isLight ? 'is-active' : ''
        }`}
        title={`${lightLabel} (Bright Mode)`}
        aria-label={`${lightLabel} (Bright Mode)`}
      >
        <Sun className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
